import { useEffect, useState } from 'react';
import { deleteField, doc, getDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { getDb } from '@talk2nick/shared';
import type { RoomTranslationMode } from '@talk2nick/core';

/**
 * 내 멤버 문서(고정/음소거) 캐시 (PLAN 6-8 / 5-8-7장).
 *
 * `members/{uid}`에는 방별 고정(`pinned`)과 음소거(`mutedUntil`)가 들어 있다.
 * 방마다 onSnapshot을 걸면 리스너가 방 개수만큼 늘어나므로(PLAN 5-8-9장 예산),
 * 목록에 보이는 방만 세션당 1회 `getDoc`으로 읽어 모듈 캐시에 담는다.
 * 내가 고정/음소거를 바꾸면 서버 왕복 없이 캐시를 먼저 갱신한다(낙관적).
 */
export interface MembershipPatch {
  pinned?: boolean;
  /** Timestamp 대신 toDate()만 요구한다 — SDK 클래스와의 타입 충돌을 피한다 */
  mutedUntil?: { toDate(): Date };
  /** 기존 멤버 조회에서 함께 읽어 로컬 미읽음 배지를 보정한다. */
  lastReadAt?: { toDate(): Date };
  /** 방별 번역 모드 (목록의 原文 뱃지·알림 번역 판정용, PLAN 5-7장) */
  translationMode?: RoomTranslationMode;
  /** 내가 이 방에서 나갔는가 — 나간 방은 목록에 남고 읽기 전용 배지가 붙는다 */
  leftAt?: { toDate(): Date };
  /** 강제퇴장시킨 관리자 (자발적 퇴장과 구분하는 배지용) */
  removedBy?: string;
  /** 내가 이 방을 목록에서 숨겼는가 — 보관 섹션으로만 보인다 */
  archived?: boolean;
}

const cache = new Map<string, MembershipPatch>();
let cacheUid: string | null = null;
const inFlight = new Set<string>();
const listeners = new Set<() => void>();

/** 영구 음소거는 먼 미래 시각으로 표현한다 (Timestamp 필드 하나로 끝내려고) */
export const MUTE_FOREVER_MS = 100 * 365 * 24 * 3600_000;

/** 음소거 선택지 (PLAN 5-8-7장: 1시간/8시간/1일/영구) */
export const MUTE_DURATIONS_MS = {
  hour: 3600_000,
  eightHours: 8 * 3600_000,
  day: 86_400_000,
  forever: MUTE_FOREVER_MS,
} as const;

function notify(): void {
  for (const listener of listeners) listener();
}

function memberRef(convoId: string, uid: string) {
  return doc(getDb(), 'convos', convoId, 'members', uid);
}

/** 내가 바꾼 값을 캐시에 먼저 반영한다 — 목록이 즉시 갱신된다 */
export function patchMembership(convoId: string, patch: MembershipPatch): void {
  cache.set(convoId, { ...cache.get(convoId), ...patch });
  notify();
}

export function getMembership(convoId: string): MembershipPatch | undefined {
  return cache.get(convoId);
}

function snapshotOf(convoIds: string[]): Map<string, MembershipPatch> {
  const next = new Map<string, MembershipPatch>();
  for (const convoId of convoIds) {
    const member = cache.get(convoId);
    if (member) next.set(convoId, member);
  }
  return next;
}

/**
 * 목록에 보이는 방들의 내 멤버 문서를 채운다.
 * 이미 캐시에 있으면 다시 읽지 않는다 (읽기 최소화).
 */
export function useMemberships(
  uid: string | null,
  convoIds: string[],
): Map<string, MembershipPatch> {
  const key = [...convoIds].sort().join(',');
  const [snapshot, setSnapshot] = useState(() => snapshotOf(convoIds));

  // 캐시가 바뀔 때(내 쓰기/조회 완료)마다 다시 계산한다
  useEffect(() => {
    const unsubscribe = (() => {
      const cb = () => setSnapshot(snapshotOf(key === '' ? [] : key.split(',')));
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    })();
    return unsubscribe;
  }, [key]);

  useEffect(() => {
    const targets = key === '' ? [] : key.split(',');
    if (cacheUid !== uid) {
      cacheUid = uid;
      cache.clear();
      inFlight.clear();
      notify();
    }
    setSnapshot(snapshotOf(targets));
    if (!uid) return;

    const missing = targets.filter((convoId) => !cache.has(convoId) && !inFlight.has(convoId));
    if (missing.length === 0) return;

    for (const convoId of missing) inFlight.add(convoId);
    let cancelled = false;

    void (async () => {
      await Promise.all(
        missing.map(async (convoId) => {
          try {
            const snap = await getDoc(memberRef(convoId, uid));
            if (cacheUid !== uid) return;
            if (snap.exists()) {
              const data = snap.data() as {
                pinned?: boolean;
                mutedUntil?: { toDate(): Date };
                lastReadAt?: { toDate(): Date };
                translation?: { mode?: RoomTranslationMode };
                leftAt?: { toDate(): Date };
                removedBy?: string;
                archived?: boolean;
              };
              cache.set(convoId, {
                pinned: data.pinned,
                mutedUntil: data.mutedUntil,
                lastReadAt: data.lastReadAt,
                translationMode: data.translation?.mode,
                leftAt: data.leftAt,
                removedBy: data.removedBy,
                archived: data.archived,
              });
            }
          } catch (error) {
            console.error('[talk2nIcK] 멤버 문서 조회 실패:', error);
          } finally {
            if (cacheUid === uid) inFlight.delete(convoId);
          }
        }),
      );
      if (cacheUid === uid) notify();
      if (!cancelled && cacheUid === uid) setSnapshot(snapshotOf(targets));
    })();

    return () => {
      cancelled = true;
    };
  }, [key, uid]);

  return cacheUid === uid ? snapshot : new Map();
}

/** 방 고정/해제 (내 멤버 문서만 — Rules가 본인 필드로 제한한다) */
export async function setConvoPinned(uid: string, convoId: string, pinned: boolean): Promise<void> {
  patchMembership(convoId, { pinned });
  await updateDoc(memberRef(convoId, uid), { pinned });
}

/**
 * 방 숨기기/보이기.
 *
 * 목록에서 빼고 보관 섹션으로만 보여 준다. 폭파·퇴장으로 남은 읽기 전용 방을
 * 치우는 용도라, 방 문서나 권한은 건드리지 않고 내 멤버 문서 한 필드만 바꾼다.
 */
export async function setConvoArchived(
  uid: string,
  convoId: string,
  archived: boolean,
): Promise<void> {
  patchMembership(convoId, { archived });
  await updateDoc(memberRef(convoId, uid), { archived });
}

/** 음소거 설정/해제. `untilMs`가 null이면 해제 */
export async function setConvoMuted(
  uid: string,
  convoId: string,
  untilMs: number | null,
): Promise<void> {
  const until = untilMs === null ? null : new Date(untilMs);
  patchMembership(convoId, {
    mutedUntil: untilMs === null ? undefined : { toDate: () => new Date(untilMs) },
  });
  await updateDoc(memberRef(convoId, uid), {
    // 필드를 지워야 해제가 서버에서도 확실해진다
    mutedUntil: untilMs === null ? deleteField() : until,
  });
}

/** 여러 방 음소거 일괄 적용 (목록 다중선택 → 🔕) */
export async function setConvoMutedMany(
  uid: string,
  convoIds: string[],
  untilMs: number | null,
): Promise<number> {
  if (convoIds.length === 0) return 0;
  const until = untilMs === null ? null : new Date(untilMs);
  const db = getDb();
  const batch = writeBatch(db);

  for (const convoId of convoIds) {
    patchMembership(convoId, {
      mutedUntil: untilMs === null ? undefined : { toDate: () => new Date(untilMs) },
    });
    batch.update(memberRef(convoId, uid), {
      mutedUntil: untilMs === null ? deleteField() : until,
    });
  }

  await batch.commit();
  return convoIds.length;
}

/** 여러 방 고정/해제 일괄 적용 (목록 다중선택 → 📌) */
export async function setConvoPinnedMany(
  uid: string,
  convoIds: string[],
  pinned: boolean,
): Promise<number> {
  if (convoIds.length === 0) return 0;
  const db = getDb();
  const batch = writeBatch(db);

  for (const convoId of convoIds) {
    patchMembership(convoId, { pinned });
    batch.update(memberRef(convoId, uid), { pinned });
  }

  await batch.commit();
  return convoIds.length;
}

/** 음소거 중인가 (PLAN 5-8-7장 — 목록/배지는 갱신하고 알림만 숨긴다) */
export function isMuted(member: MembershipPatch | undefined, now: number = Date.now()): boolean {
  const until = member?.mutedUntil;
  if (!until) return false;
  return until.toDate().getTime() > now;
}
